import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Menu, X, ChevronDown, Search, ArrowRight } from "lucide-react";
import logo from "@/assets/apex-av-new-logo-optimized.webp";
import { serviceAreas } from "@/data/serviceAreas";
import { blogPosts } from "@/data/blogPosts";
import { Button } from "@/components/ui/button";
import { logSiteSearch } from "@/lib/siteSearch.functions";

const services = [
  { to: "/services/home-theater", label: "Custom Home Theater" },
  { to: "/services/dolby-atmos-home-theater", label: "Dolby Atmos Home Theater" },
  { to: "/services/smart-home", label: "Smart Home Automation" },
  { to: "/services/control4", label: "Control4 Home Automation" },
  { to: "/services/whole-home-audio", label: "Whole-Home Audio" },
  { to: "/services/hidden-audio", label: "Hidden & Invisible Audio" },
  { to: "/services/whole-home-video", label: "Whole-Home Video" },
  { to: "/services/outdoor", label: "Outdoor Entertainment" },
  { to: "/services/climate-control", label: "Smart Climate Control" },
  { to: "/services/lighting", label: "Smart Lighting" },
  { to: "/services/shading", label: "Motorized Shading" },
  { to: "/services/security", label: "Security & Surveillance" },
  { to: "/services/networking", label: "Networking & Wi-Fi" },
  { to: "/services/commercial-av", label: "Commercial AV" },
  { to: "/services/conference", label: "Conference & Office AV" },
  { to: "/services/prewire", label: "New Construction Prewire" },
  { to: "/services/retrofit", label: "Retrofit & Cable Fishing" },
  { to: "/services/tv-mounting", label: "TV Mounting" },
  { to: "/services/av-installation", label: "AV Installation" },
  { to: "/services/voice-control", label: "Voice Control" },
] as const;

const simpleNav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
] as const;

const corePages = [
  { to: "/", label: "Home", detail: "Apex AV home automation and AV installation" },
  { to: "/services", label: "Services", detail: "Explore every installation service" },
  { to: "/service-areas", label: "Service Areas", detail: "Cities and neighborhoods we serve" },
  { to: "/about", label: "About Apex AV", detail: "Meet your local Denver AV team" },
  { to: "/blog", label: "Guides & Articles", detail: "Practical home technology advice" },
  { to: "/contact", label: "Contact & Free Consultation", detail: "Tell us about your project" },
] as const;

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [requestSentFor, setRequestSentFor] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!searchOpen) return;
    searchInputRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchOpen]);

  const normalizedQuery = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!normalizedQuery) return { pages: corePages.slice(0, 4), services: [], areas: [], articles: [] };
    const includes = (...values: string[]) => values.some((value) => value.toLowerCase().includes(normalizedQuery));
    return {
      pages: corePages.filter((item) => includes(item.label, item.detail)).slice(0, 4),
      services: services.filter((item) => includes(item.label)).slice(0, 6),
      areas: serviceAreas.filter((item) => includes(item.name, item.blurb, item.zip)).slice(0, 5),
      articles: blogPosts.filter((item) => includes(item.title, item.description, item.category, item.keywords)).slice(0, 4),
    };
  }, [normalizedQuery]);

  const resultCount = matches.pages.length + matches.services.length + matches.areas.length + matches.articles.length;

  // Record what visitors search for (debounced so we log finished thoughts, not keystrokes).
  const loggedRef = useRef<string>("");
  useEffect(() => {
    if (!searchOpen) return;
    if (normalizedQuery.length < 2) return;
    if (loggedRef.current === normalizedQuery) return;
    const timer = window.setTimeout(() => {
      loggedRef.current = normalizedQuery;
      void logSiteSearch({
        query: query.trim(),
        resultsCount: resultCount,
        path: window.location.pathname,
      });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [normalizedQuery, query, resultCount, searchOpen]);

  // Clear the "request sent" confirmation as soon as the visitor edits their term.
  useEffect(() => {
    if (requestSentFor !== null && requestSentFor !== normalizedQuery) setRequestSentFor(null);
  }, [normalizedQuery, requestSentFor]);

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
    setRequestSentFor(null);
    loggedRef.current = "";
  };

  // A visitor asked for something we don't cover yet — record it as a request.
  const submitMissingTerm = () => {
    const term = query.trim();
    if (!term) return;
    setRequestSentFor(normalizedQuery);
    loggedRef.current = normalizedQuery;
    void logSiteSearch({
      query: term,
      resultsCount: 0,
      path: window.location.pathname,
      clickedPath: "requested:" + term.slice(0, 120),
    });
  };

  const handleResultClick = (clickedPath: string) => {
    if (normalizedQuery.length >= 1) {
      loggedRef.current = normalizedQuery;
      void logSiteSearch({
        query: query.trim(),
        resultsCount: resultCount,
        path: window.location.pathname,
        clickedPath,
      });
    }
    closeSearch();
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
      <div className="container mx-auto flex h-20 items-center justify-between gap-3 px-6 md:h-24 lg:gap-6">
        <Link to="/" className="flex items-center group relative shrink-0">
          <img
            src={logo}
            alt="Apex AV Denver smart home automation and audio video installation logo"
            width={200}
            height={200}
            loading="eager"
            decoding="async"
            className="h-14 w-auto shrink-0 object-contain transition-opacity duration-300 group-hover:opacity-80 sm:h-16 md:h-[4.5rem]"
          />
        </Link>
        <nav className="hidden min-w-0 items-center gap-3 text-[0.68rem] uppercase tracking-[0.18em] md:flex lg:gap-7">

          <Link
            to="/"
            className="link-elegant font-medium text-muted-foreground hover:text-brand-text transition-colors"
            activeProps={{ className: "text-foreground" }}
            activeOptions={{ exact: true }}
          >
            Home
          </Link>

          {/* Services dropdown */}
          <div className="group relative shrink-0">
            <Link
              to="/services"
              className="link-elegant inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-medium text-muted-foreground transition-colors hover:text-brand-text"
              activeProps={{ className: "text-foreground" }}
            >
              Services
              <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-hover:rotate-180" />
            </Link>
            <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[26rem]">
              <div className="grid grid-cols-2 gap-0.5 border border-border bg-popover/95 p-2 shadow-card backdrop-blur-xl">
                {services.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className="px-3 py-2 text-sm normal-case tracking-normal text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  >
                    {s.label}
                  </Link>
                ))}
                <Link
                  to="/services"
                  className="col-span-2 mt-1 border-t border-border px-3 py-2 text-sm font-semibold normal-case tracking-normal text-brand-text transition-colors hover:bg-accent"
                >
                  View all services →
                </Link>
              </div>
            </div>
          </div>

          {/* Service Areas dropdown */}
          <div className="group relative shrink-0">
            <Link
              to="/service-areas"
              className="link-elegant inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-medium text-muted-foreground transition-colors hover:text-brand-text"
              activeProps={{ className: "text-foreground" }}
            >
              Service Areas
              <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-hover:rotate-180" />
            </Link>
            <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[26rem]">
              <div className="grid max-h-[70vh] grid-cols-2 gap-0.5 overflow-y-auto border border-border bg-popover/95 p-2 shadow-card backdrop-blur-xl">
                {serviceAreas.map((a) => (
                  <Link
                    key={a.slug}
                    to="/service-areas/$slug"
                    params={{ slug: a.slug }}
                    className="px-3 py-2 text-sm normal-case tracking-normal text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  >
                    {a.name}
                  </Link>
                ))}
                <Link
                  to="/service-areas"
                  className="col-span-2 mt-1 border-t border-border px-3 py-2 text-sm font-semibold normal-case tracking-normal text-brand-text transition-colors hover:bg-accent"
                >
                  View all areas →
                </Link>
              </div>
            </div>
          </div>

          <Link
            to="/about"
            className="link-elegant font-medium text-muted-foreground hover:text-brand-text transition-colors"
            activeProps={{ className: "text-foreground" }}
          >
            About
          </Link>
          <Link
            to="/blog"
            className="link-elegant font-medium text-muted-foreground hover:text-brand-text transition-colors"
            activeProps={{ className: "text-foreground" }}
          >
            Blog
          </Link>
          <Link
            to="/contact"
            className="link-elegant font-medium text-muted-foreground hover:text-brand-text transition-colors"
            activeProps={{ className: "text-foreground" }}
          >
            Contact
          </Link>
        </nav>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Search the site"
            aria-expanded={searchOpen}
            className="h-11 w-11 rounded-none border border-border bg-card/40 text-muted-foreground hover:text-brand-text"
            onClick={() => {
              setOpen(false);
              setSearchOpen((current) => !current);
            }}
          >
            <Search className="h-4 w-4" />
          </Button>
          <Link
            to="/contact"
            className="interactive-lift hidden min-h-11 items-center justify-center border border-brand/50 bg-primary px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-primary-foreground shadow-glow transition hover:bg-brand-glow lg:inline-flex"
          >
            Get a Quote
          </Link>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Toggle menu"
            aria-expanded={open}
            className="h-11 w-11 rounded-none bg-card/80 md:hidden"
            onClick={() => {
              setSearchOpen(false);
              setOpen(!open);
            }}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>
      {searchOpen && (
        <div className="fixed left-0 right-0 top-20 z-50 max-h-[calc(100svh-5rem)] overflow-y-auto border-y border-border bg-background shadow-card backdrop-blur-xl md:absolute md:top-full md:z-auto md:max-h-none md:w-full md:overflow-visible md:bg-background/98">
          <div className="container mx-auto px-6 py-5 md:py-7">
            <div className="relative mx-auto max-w-4xl">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand-text" />
              <input
                ref={searchInputRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search services, areas, and guides…"
                aria-label="Search services, areas, and guides"
                className="h-14 w-full border border-border bg-card/70 pl-12 pr-14 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-brand/70 focus:ring-1 focus:ring-brand/40"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Close search"
                className="absolute right-1.5 top-1/2 h-11 w-11 -translate-y-1/2 rounded-none text-muted-foreground hover:text-foreground"
                onClick={closeSearch}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="mx-auto mt-4 max-h-[min(60vh,32rem)] max-w-4xl overflow-y-auto border border-border bg-card/40 p-2">
              {!normalizedQuery && (
                <p className="px-3 py-2 text-xs uppercase text-muted-foreground">Popular pages</p>
              )}
              {resultCount === 0 ? (
                <div className="px-4 py-8 text-center">
                  {requestSentFor === normalizedQuery ? (
                    <>
                      <p className="font-display text-lg text-foreground">Thanks — we heard you!</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        We saved “{query.trim()}” and will keep it in mind as we add new services and guides. If it's about a
                        project, we're happy to talk — reach out any time.
                      </p>
                      <Link
                        to="/contact"
                        onClick={closeSearch}
                        className="mt-4 inline-flex min-h-11 items-center justify-center border border-brand/50 bg-primary px-5 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary-foreground transition hover:bg-brand-glow"
                      >
                        Contact us
                      </Link>
                    </>
                  ) : (
                    <>
                      <p className="font-display text-lg text-foreground">Hmm, we don't have a page for that yet.</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        That doesn't mean we can't help — tell us what you were looking for and we'll take note.
                      </p>
                      <Button
                        type="button"
                        onClick={submitMissingTerm}
                        className="mt-4 min-h-11 rounded-none border border-brand/50 bg-primary px-5 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary-foreground hover:bg-brand-glow"
                      >
                        Send “{query.trim()}” to our team
                      </Button>
                      <p className="mt-3 text-xs text-muted-foreground">Or try a service, city, or topic such as home theater.</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid gap-1 md:grid-cols-2">
                  {matches.pages.map((item) => (
                    <Link key={item.to} to={item.to} onClick={() => handleResultClick(item.to)} className="group flex min-h-14 items-center justify-between gap-4 px-3 py-2.5 transition-colors hover:bg-accent">
                      <span><span className="block text-sm font-medium text-foreground">{item.label}</span><span className="block text-xs text-muted-foreground">Page · {item.detail}</span></span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-brand-text transition-transform group-hover:translate-x-1" />
                    </Link>
                  ))}
                  {matches.services.map((item) => (
                    <Link key={item.to} to={item.to} onClick={() => handleResultClick(item.to)} className="group flex min-h-14 items-center justify-between gap-4 px-3 py-2.5 transition-colors hover:bg-accent">
                      <span><span className="block text-sm font-medium text-foreground">{item.label}</span><span className="block text-xs text-muted-foreground">Service</span></span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-brand-text transition-transform group-hover:translate-x-1" />
                    </Link>
                  ))}
                  {matches.areas.map((item) => (
                    <Link key={item.slug} to="/service-areas/$slug" params={{ slug: item.slug }} onClick={() => handleResultClick(`/service-areas/${item.slug}`)} className="group flex min-h-14 items-center justify-between gap-4 px-3 py-2.5 transition-colors hover:bg-accent">
                      <span><span className="block text-sm font-medium text-foreground">{item.name}</span><span className="block text-xs text-muted-foreground">Service area · {item.zip}</span></span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-brand-text transition-transform group-hover:translate-x-1" />
                    </Link>
                  ))}
                  {matches.articles.map((item) => (
                    <Link key={item.slug} to="/blog/$slug" params={{ slug: item.slug }} onClick={() => handleResultClick(`/blog/${item.slug}`)} className="group flex min-h-14 items-center justify-between gap-4 px-3 py-2.5 transition-colors hover:bg-accent">
                      <span><span className="block text-sm font-medium text-foreground">{item.title}</span><span className="block text-xs text-muted-foreground">Guide · {item.category}</span></span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-brand-text transition-transform group-hover:translate-x-1" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {open && (
        <div className="max-h-[calc(100svh-5rem)] overflow-y-auto border-t border-border bg-background/98 backdrop-blur-xl md:hidden">
          <nav className="container mx-auto flex flex-col gap-1 px-6 py-5">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setSearchOpen(true);
              }}
              className="mb-2 flex min-h-12 w-full items-center gap-3 border border-border bg-card/60 px-4 text-sm text-muted-foreground"
            >
              <Search className="h-4 w-4 text-brand-text" />
              Search services, areas, and guides
            </button>
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-muted-foreground hover:text-foreground py-2"
            >
              Home
            </Link>

            <button
              type="button"
              onClick={() => setMobileSection(mobileSection === "services" ? null : "services")}
              className="flex items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground py-2"
            >
              Services
              <ChevronDown
                className={`h-4 w-4 transition-transform ${mobileSection === "services" ? "rotate-180" : ""}`}
              />
            </button>
            {mobileSection === "services" && (
              <div className="pl-3 pb-2 flex flex-col gap-1 border-l border-border ml-1">
                <Link
                  to="/services"
                  onClick={() => setOpen(false)}
                  className="text-sm py-1.5 text-brand-text font-semibold"
                >
                  All services
                </Link>
                {services.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    onClick={() => setOpen(false)}
                    className="text-sm py-1.5 text-muted-foreground hover:text-foreground"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setMobileSection(mobileSection === "areas" ? null : "areas")}
              className="flex items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground py-2"
            >
              Service Areas
              <ChevronDown
                className={`h-4 w-4 transition-transform ${mobileSection === "areas" ? "rotate-180" : ""}`}
              />
            </button>
            {mobileSection === "areas" && (
              <div className="pl-3 pb-2 flex flex-col gap-1 border-l border-border ml-1">
                <Link
                  to="/service-areas"
                  onClick={() => setOpen(false)}
                  className="text-sm py-1.5 text-brand-text font-semibold"
                >
                  All areas
                </Link>
                {serviceAreas.map((a) => (
                  <Link
                    key={a.slug}
                    to="/service-areas/$slug"
                    params={{ slug: a.slug }}
                    onClick={() => setOpen(false)}
                    className="text-sm py-1.5 text-muted-foreground hover:text-foreground"
                  >
                    {a.name}
                  </Link>
                ))}
              </div>
            )}

            {simpleNav.slice(1).map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="text-sm font-medium text-muted-foreground hover:text-foreground py-2"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex min-h-12 items-center justify-center bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              Get a Quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
